import React, {
  useEffect,
  useContext,
  useMemo,
  useState,
  useCallback,
  useRef,
  lazy,
  Suspense,
} from "react";

import { Image, Link } from "uikit-react";
import UIkit from "uikit";
import moment from "moment";
import classnames from "classnames";
import { useHistory } from "react-router-dom";
import clsx from "clsx";
import InfiniteScroll from "react-infinite-scroll-component";
import { httpV2 } from "../../http.js";
import feather from "feather-icons";
import ChatbotService from "../../services/chatbot.service.js";

import { AppContexts } from "../../providers/";
import {
  sendAirBnBMessage,
  sendAirBnBImageMessage,
  airbnbUploadFile,
} from "../../services/airbnb.service.js";

import Inbox from "./inbox";
import Responses from "../common/Responses";
import AddTextLine from "../common/AddTextLine";

import GuestDetails from "./detail-cards/guest-card.js";
import AirbnbDetails from "./detail-cards/airbnb-card.js";

import WorkOrderAdd from "../work_order/work_order_add";
import NoteAddContent from "../notes/note-add/note-add-content";
import EmojiPicker from "./emoji-picker";
import InternalNoteOffcanvas from "./InternalNoteOffcanvas";
import FeatherIcon from "../common/FeatherIcon";
import {
  MessageLengthEditorStatus,
  MessageLengthInlineStatus,
} from "../common/SmsMessageLengthStatus";
import { selectPreferredReservation } from "./reservation-context";
import { optimizeMessageImage } from "../../utils/messageImage";
import {
  analyzeSmsMessage,
  normalizeSmsPaste,
} from "../../utils/smsMessageLength";

import { IncomingStatus } from "ruebarue-utils/thread-status.js";

// import UtilsService from "../../services/util.service.js";

const NewMessageModal = lazy(() => import("./new-message/NewMessageModal"));

const msgClasses = {
  automated: "rbr-msg-automated",
  system: "rbr-msg-automated",
  internal: "rbr-msg-internal-note",
  user: "rbr-msg-outbound",
  recipient: "rbr-msg-outbound",
};

const sourceLabels = {
  sms: "SMS",
  airbnb: "AirBnB",
};

const SMS_CHARACTER_LIMIT = 612;

const Messages = () => {
  const workOrderContext = useContext(AppContexts.WorkOrderContext);
  const {
    threadDetail,
    selectedThreadId,
    selectThread,
    selectPaginatedThread,
    selectPersonalPaginatedThread,
    sendMessage,
    appendOutgoingMessage,
    removeOutgoingMessage,
    loading,
    isSendingFlag,
    refresh,
    personalInbox,
    setSelectedThreadId,
    reservation,
    setThreadDetail,
    loadMoreMessages,
    loadMorePersonalMessages,
    messageOffsetRef,
    sendInternalNote,

    legacy,
    setLegacy,
  } = useContext(AppContexts.InboxContext);

  // const { pushToRoom } = UtilsService;

  const { inbox, user } = useContext(AppContexts.AuthContext);

  const history = useHistory();

  const { listReservations } = useContext(AppContexts.ReservationsContext);

  const { genUUID, uploadFile, socket, userStatus, setStatus } = useContext(
    AppContexts.UtilContext,
  );

  // const { getNoteByItem, notes } = useContext(AppContexts.NotesContext);

  const [sending, setSending] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [sendText, setSendText] = useState("");
  const [granted, setGranted] = useState(
    window.Notification?.permission || "default",
  );
  const [msgId, setMsgId] = useState(null);
  const [attachment, setAttachment] = useState(null);
  const [isInputValid, setIsInputValid] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [sendTo, setSendTo] = useState(null);
  const chatPanelRef = useRef(null);
  const firstMsgRef = useRef(null);
  const inputRef = useRef(null);

  const [newMsgOpen, setNewMsgOpen] = useState(false);

  const [aiOpen, setAiOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiQuestion, setAiQuestion] = useState("");
  const [aiAnswer, setAiAnswer] = useState("");
  const [aiSource, setAiSource] = useState("");
  const [aiError, setAiError] = useState("");
  const [smsOptimization, setSmsOptimization] = useState(null);
  const [smsOptimizationLoading, setSmsOptimizationLoading] = useState(false);
  const [smsOptimizationError, setSmsOptimizationError] = useState("");
  const [internalNoteOpen, setInternalNoteOpen] = useState(false);
  const [internalNoteText, setInternalNoteText] = useState("");
  const [internalNoteAttachments, setInternalNoteAttachments] = useState([]);
  const [internalNoteSaving, setInternalNoteSaving] = useState(false);
  const [internalNoteError, setInternalNoteError] = useState("");

  const accountId = user?.account_id;
  const userId = user?.user?.user_id;
  const inboxId = inbox?.inbox_id;

  const openAiModal = () => setAiOpen(true);
  const closeAiModal = () => {
    setAiOpen(false);
    setAiError("");
    setAiSource("");
  };

  const resetInternalNoteComposer = useCallback(() => {
    setInternalNoteText("");
    setInternalNoteAttachments([]);
    setInternalNoteError("");
  }, []);

  const closeInternalNote = useCallback(() => {
    setInternalNoteOpen(false);
    setInternalNoteSaving(false);
    resetInternalNoteComposer();
  }, [resetInternalNoteComposer]);

  const insertAiResponse = () => {
    if (!aiAnswer) return;
    setSendText(aiAnswer);
    setIsInputValid(aiAnswer.trim() !== "" || !!attachment);
    closeAiModal();

    // optional: focus textarea after inserting
    setTimeout(() => {
      const el = document.querySelector(".hms-msg-typing-box textarea");
      el?.focus?.();
    }, 50);
  };

  const fetchAiSuggestion = async (guestMessageText) => {
    const q = (guestMessageText || "").trim();
    if (!q) return;

    if (!accountId) {
      UIkit.notification("Missing account context", "warning");
      return;
    }

    if (!userId) {
      UIkit.notification("Missing user context", "warning");
      return;
    }

    if (!inboxId) {
      UIkit.notification("Missing inbox context", "warning");
      return;
    }

    if (!formattedPhone) {
      UIkit.notification("Missing guest phone for AI suggestion", "warning");
      return;
    }

    setAiLoading(true);
    setAiError("");
    setAiQuestion(q);
    setAiAnswer("");
    setAiSource("");
    openAiModal();

    try {
      const res = await httpV2.post("/ai/guest-reply", {
        account_id: String(accountId),
        user_id: String(userId),
        inbox_id: String(inboxId),
        formatted_phone: String(formattedPhone),
        question: q,
      });

      const data = res.data || {};

      setAiAnswer(data?.answer || "");
      setAiSource(data?.source || "");
    } catch (e) {
      const data = e.response?.data || {};
      const msg =
        data?.answer || data?.error || "Network error generating AI reply.";
      setAiError(msg);
      setAiSource(data?.source || "");
    } finally {
      setAiLoading(false);
    }
  };

  // useMemo for messages and related data (move this up)
  const { messages, guestReservations, contact, guestId, guest, sources } =
    useMemo(() => {
      let sms = !!threadDetail.guest_reservation || !!threadDetail.contact;
      let sources = threadDetail.sources || [];

      if (!!sms) {
        sources.push({
          thread_id: threadDetail.thread_id,
          ota: "sms",
          ota_id: null,
        });
      }

      return {
        messages: threadDetail?.messages || [],
        contact: threadDetail?.contact || {},
        guestReservations: (threadDetail?.guest_reservations || []).filter(
          (r) => !r.deleted,
        ),
        guestId: threadDetail?.guest_id,
        guest: threadDetail?.guest,
        sources: sources,
      };
    }, [threadDetail]);

  const lastMsgSource = useMemo(() => {
    return (messages || []).slice(-1)[0]?.message_source || "sms";
  }, [messages]);

  const carrier = useMemo(() => {
    return !!sources.find((s) => s?.ota === "airbnb") ? "airbnb" : "sms";
  }, [sources]);

  const messageLength = useMemo(() => {
    const currentReservation = selectPreferredReservation(guestReservations);

    return analyzeSmsMessage(sendText, {
      FirstName:
        guest?.first_name || contact?.first_name || currentReservation?.first_name,
      LastName:
        guest?.last_name || contact?.last_name || currentReservation?.last_name,
      Email: guest?.email || contact?.email || currentReservation?.email,
      PhoneNumber:
        guest?.formatted_phone ||
        guest?.phone ||
        contact?.formatted_phone ||
        contact?.phone ||
        currentReservation?.formatted_phone ||
        currentReservation?.phone,
      PropertyId: currentReservation?.pms_id,
      PropertyAddress: currentReservation?.rental_address,
      CheckIn: currentReservation?.check_in,
      CheckOut: currentReservation?.check_out,
      ReservationId:
        currentReservation?.reservation_id || currentReservation?.native_id,
      DoorCode: currentReservation?.door_code,
    });
  }, [contact, guest, guestReservations, sendText]);

  const formattedPhone =
    threadDetail?.guest?.formatted_phone || guest?.formatted_phone || null;

  const personalInboxId = useMemo(() => {
    if (!!(user.user.personal_inboxes || [])[0]) {
      return user.user.personal_inboxes[0].id;
    }

    return null;
  }, [user]);

  const readStatus = useMemo(() => {
    let readBy = [];

    for (let pid in userStatus) {
      if ((userStatus[pid]?.read || []).includes(+threadDetail.id)) {
        readBy.push(+pid);
      }
    }

    (threadDetail.group_status || []).map((s) => {
      if (!(s.tokens || []).includes("unread")) {
        readBy.push(+s.personal_inbox_id);
      }
    });

    // TODO make JSX and filter for user's inbox

    return [...new Set(readBy.filter((id) => +id !== +personalInboxId))];
  }, [userStatus, threadDetail]);

  const viewingStatus = useMemo(() => {
    return Object.keys(userStatus).reduce((a, id) => {
      if (+id === +personalInboxId) {
        return a;
      }

      if (userStatus[id].viewing === +threadDetail.id) {
        a.push(+id);
      }

      return a;
    }, []);
  }, [userStatus, threadDetail]);

  const typingStatus = useMemo(() => {
    return Object.keys(userStatus).reduce((a, id) => {
      if (+id === +personalInboxId) {
        return a;
      }

      if (userStatus[id].typing === +threadDetail.id) {
        a.push(+id);
      }

      return a;
    }, []);
  }, [userStatus, threadDetail]);

  const personalInboxName = (pid) => {
    let { recipients } = inbox;
    let recipient = (recipients || []).find((r) => {
      return (r.personal_inboxes || []).find((pi) => {
        return +pi.personal_inbox_id === +pid;
      });
    });
    let name =
      `${recipient?.first_name || ""} ${recipient?.last_name || ""}`.trim();

    return name || "A team member";
  };

  const handleNewMessageSent = useCallback(
    (threadId) => {
      setNewMsgOpen(false);
      if (threadId) {
        history.push(`/messages/${threadId}`);
        if (personalInboxId !== null) {
          personalInbox(personalInboxId, 0, "");
          selectPersonalPaginatedThread(threadId, personalInboxId);
        } else {
          refresh(0, "");
          selectPaginatedThread(threadId);
        }
      } else {
        if (personalInboxId !== null) {
          personalInbox(personalInboxId, 0, "");
        } else {
          refresh(0, "");
        }
      }
    },
    [
      personalInboxId,
      history,
      personalInbox,
      refresh,
      selectPaginatedThread,
      selectPersonalPaginatedThread,
    ],
  );

  // Reset pagination state when thread changes
  useEffect(() => {
    setHasMore(true);
    setIsLoadingMore(false);
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  }, [selectedThreadId]);

  // Handler for InfiniteScroll
  const handleLoadMore = async () => {
    setIsLoadingMore(true);
    const limit = 30;
    let apiCount;
    if (personalInboxId !== null) {
      apiCount = await loadMorePersonalMessages(
        selectedThreadId,
        personalInboxId,
        messageOffsetRef.current,
        limit,
      );
    } else {
      apiCount = await loadMoreMessages(
        selectedThreadId,
        messageOffsetRef.current,
        limit,
      );
    }
    setIsLoadingMore(false);
    if (apiCount < limit) {
      setHasMore(false);
    }
  };

  useEffect(() => {
    document.title = "Messages - RueBaRue";

    setSelectedThreadId(null);
    setThreadDetail({});
    let threadId = +window.location.pathname.split("/")[2];

    if (!!threadId) {
      if (personalInboxId !== null) {
        selectPersonalPaginatedThread(threadId, personalInboxId);
      } else {
        selectPaginatedThread(threadId);
      }
    }
  }, []);

  useEffect(() => {
    if (!selectedThreadId) {
      return;
    }

    let msg = {
      thread_id: +selectedThreadId,
      personal_inbox_id: personalInboxId,
      status: !!sendText.trim() ? "typing" : "viewing",
    };

    let current = userStatus[personalInboxId] || null;
    if (!current) {
      return;
    }

    let changed = false;
    if (msg.status === "typing" && current.typing === null) {
      changed = true;
    }

    if (msg.status === "viewing" && current.typing !== null) {
      changed = true;
    }

    if (changed) {
      window.dispatchEvent(
        new IncomingStatus({
          action: "update_status",
          data: msg,
        }),
      );

      // pushToRoom("update_status", msg);
      // setStatus(user, {
      //   action: "update_status",
      //   data: msg,
      // });
    }
  }, [sendText]);

  useEffect(() => {
    setSendText("");
    setAttachment(null); // Clear attachment when switching threads
    setSmsOptimization(null);
    setSmsOptimizationError("");
  }, [selectedThreadId]);

  useEffect(() => {
    if (
      smsOptimization &&
      sendText !== smsOptimization.original_message &&
      sendText !== smsOptimization.suggested_message
    ) {
      setSmsOptimization(null);
    }
  }, [sendText, smsOptimization]);

  useEffect(() => {
    if (!aiOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        closeAiModal();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [aiOpen]);

  // useEffect(() => {
  //   if (threadDetail) {
  //     let contactId =  Number(threadDetail.contact?.id);
  //     let guestId = Number(threadDetail.guest_id);
  //     if (contactId || guestId) {
  //       getNoteByItem(contactId || guestId, contactId ? 'contact' : 'guest');
  //     }
  //   };
  // }, [threadDetail]);

  const handleNotification = () => {
    console.log(
      "[Notif] clicked, current granted:",
      granted,
      "Notification API:",
      !!window.Notification,
      "current browser permission:",
      window.Notification?.permission,
    );
    if (!window.Notification) {
      console.warn("[Notif] Notification API not available");
      return;
    }
    try {
      const result = window.Notification.requestPermission((permission) => {
        // callback form (older browsers)
        console.log("[Notif] callback permission:", permission);
        setGranted(permission);
      });
      // promise form (modern browsers)
      if (result && result.then) {
        result.then((permission) => {
          console.log("[Notif] promise permission:", permission);
          setGranted(permission);
        });
      }
    } catch (err) {
      console.error("[Notif] requestPermission error:", err);
    }
  };

  const handleValidate = (e) => {
    const value =
      carrier === "sms"
        ? e.target.value.slice(0, SMS_CHARACTER_LIMIT)
        : e.target.value;
    setSendText(value);
    // Only clear error while typing; do not set error on empty until Send is clicked.
    if (!isInputValid && (value.trim() !== "" || !!attachment)) {
      setIsInputValid(true);
    }
  };

  const handleOptimizeReply = async () => {
    if (!sendText.trim() || smsOptimizationLoading) return;

    setSmsOptimizationLoading(true);
    setSmsOptimization(null);
    setSmsOptimizationError("");

    try {
      const response = await ChatbotService.optimizeMessage(
        sendText,
        "messages",
      );
      setSmsOptimization(response.data);
    } catch (error) {
      console.error("Error optimizing SMS reply:", error);
      setSmsOptimizationError(
        "We couldn't optimize this message. Please try again.",
      );
    } finally {
      setSmsOptimizationLoading(false);
    }
  };

  const applyOptimizedReply = () => {
    if (!smsOptimization?.suggested_message) return;

    setSendText(
      smsOptimization.suggested_message.slice(0, SMS_CHARACTER_LIMIT),
    );
    setIsInputValid(true);
    setSmsOptimization(null);
  };

  const handleMessagePaste = (event) => {
    if (carrier !== "sms") return;

    const pastedText = event.clipboardData.getData("text");
    const normalizedText = normalizeSmsPaste(pastedText);
    if (normalizedText === pastedText) return;

    event.preventDefault();
    const textarea = event.currentTarget;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const availableCharacters = Math.max(
      0,
      SMS_CHARACTER_LIMIT - (sendText.length - (end - start)),
    );
    const insertedText = normalizedText.slice(0, availableCharacters);
    const newValue =
      sendText.slice(0, start) + insertedText + sendText.slice(end);

    handleValidate({ target: { value: newValue } });
    setTimeout(() => {
      textarea.focus();
      textarea.selectionStart = textarea.selectionEnd =
        start + insertedText.length;
    }, 0);
  };

  const handleEmojiClick = (val) => {
    setSendText((text) =>
      carrier === "sms"
        ? `${text}${val}`.slice(0, SMS_CHARACTER_LIMIT)
        : text + val,
    );
    if (!isInputValid) {
      setIsInputValid(true);
    }
  };

  const buildAttachmentPayload = useCallback((file, url) => {
    const mimeType = file?.type || "";
    let type = "file";

    if (mimeType.startsWith("image/")) {
      type = "image";
    } else if (mimeType.startsWith("video/")) {
      type = "video";
    }

    return {
      type,
      name: file.name,
      original_name: file.name,
      url,
    };
  }, []);

  const uploadMessageAttachments = useCallback(
    async (files) => {
      return Promise.all(
        files.map(async (file) => {
          const mediaId = genUUID().replace(/-/g, "");
          const uploadUrl = await uploadFile(
            file,
            `message/attachments/${mediaId}`,
          );

          return buildAttachmentPayload(file, uploadUrl);
        }),
      );
    },
    [buildAttachmentPayload, genUUID, uploadFile],
  );

  const handleSendMessage = async (threadId) => {
    if (sending) {
      return;
    }

    if (sendText.trim() === "" && !attachment) {
      if (isInputValid) setIsInputValid(false);
      return;
    }

    setSending(true);
    let optimisticMessage = null;
    let targetThreadId = threadDetail.id || threadId;
    let sent = false;
    let preparedAttachment = attachment;

    try {
      const otaId =
        sources.find((source) => source?.ota === "airbnb")?.ota_id || null;
      const objTemp =
        threadId > 0
          ? {
              guest_reservation_id: selectPreferredReservation(
                threadDetail.guest_reservations,
              )?.guest_reservation_id,
              contact_id: threadDetail.contact_id,
              thread_id: threadDetail.id,
              message: sendText,
            }
          : {
              guest_reservation_id: reservation.guest_reservation_id,
              message: sendText,
            };

      if (attachment !== null) {
        try {
          preparedAttachment = await optimizeMessageImage(attachment);
        } catch (error) {
          console.error("Error preparing MMS image:", error);
          UIkit.notification(
            error?.message ||
              "The image could not be prepared. Please try another image.",
            "danger",
          );
          return;
        }

        objTemp.attachments = await uploadMessageAttachments([
          preparedAttachment,
        ]);
      }

      targetThreadId = objTemp.thread_id || threadId;
      optimisticMessage = {
        attachments: [...(objTemp.attachments || [])],
        client_message_id: genUUID(),
        content: objTemp.message,
        created_at: new Date().toISOString(),
        error_code: "",
        error_message: "",
        error_sent: false,
        error_sent_at: null,
        price: 0,
        inbox_id: null,
        message_id: null,
        sender_id: +user.user_id,
        sender_type: "user",
        sid: "",
        status: "sending",
        thread_id: targetThreadId,
      };
      appendOutgoingMessage(targetThreadId, optimisticMessage);

      let result;
      if (carrier === "sms") {
        result = await sendMessage(objTemp);
      } else if (carrier === "airbnb") {
        if (
          Array.isArray(objTemp.attachments) &&
          objTemp.attachments.length > 0
        ) {
          const imageMessage = { ...objTemp, message: "" };
          const textMessage = {
            ...objTemp,
            message: objTemp.message.trim(),
            attachments: null,
          };

          if (objTemp.message.trim()) {
            result = await sendAirBnBMessage(otaId, textMessage);
          }

          const [airbnbUploadUrl, mimeType, uploadError] =
            await airbnbUploadFile(otaId, preparedAttachment);
          if (uploadError !== null) throw uploadError;

          const trimmedUrl = airbnbUploadUrl.split("?")[0];
          result = await sendAirBnBImageMessage(
            otaId,
            trimmedUrl,
            mimeType,
            imageMessage,
          );
        } else if (objTemp.message.trim()) {
          result = await sendAirBnBMessage(otaId, objTemp);
        }
      }

      if (!!result && threadId === "new") {
        history.push(`/messages/${result.id}`);

        if (personalInboxId !== null) {
          personalInbox(personalInboxId, 0, "");
          selectPersonalPaginatedThread(result.id, personalInboxId);
        } else {
          refresh(0, "");
          selectPaginatedThread(result.id);
        }
      }

      sent = true;
      setAttachment(null);
      setSendText("");
    } catch (error) {
      if (optimisticMessage) {
        removeOutgoingMessage(
          targetThreadId,
          optimisticMessage.client_message_id,
        );
      }
      UIkit.notification(
        "Unable to send the message. Please try again.",
        "error",
      );
    } finally {
      setSending(false);
    }

    if (sent) {
      setTimeout(() => {
        const panel = document.querySelector(".hms-chat-panel");
        if (panel) panel.scrollTop = panel.scrollHeight;
      }, 100);
    }
  };

  const getThreadType = (thread) => {
    if (!thread.sources) {
      return "sms";
    }

    let sources = thread.sources.map((s) => s?.ota);
    if (sources.includes("airbnb")) {
      return "airbnb";
    }

    return "sms";
  };

  const handleSelectedResponse = (response) => {
    setSendText(response);
    if (!isInputValid && response.trim() !== "") {
      setIsInputValid(true);
    }
  };

  const handleInternalAttachmentChange = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) {
      return;
    }

    setInternalNoteAttachments((prev) => [...prev, ...files]);
    setInternalNoteError("");
    event.target.value = "";
  };

  const handleSaveInternalNote = async () => {
    if (!threadDetail?.id || internalNoteSaving) {
      return;
    }

    if (!internalNoteText.trim() && !internalNoteAttachments.length) {
      setInternalNoteError("Add a note or an attachment before saving.");
      return;
    }

    setInternalNoteSaving(true);
    setInternalNoteError("");

    try {
      const attachments = internalNoteAttachments.length
        ? await uploadMessageAttachments(internalNoteAttachments)
        : null;

      const payload = {
        thread_id: threadDetail.id,
        message: internalNoteText,
        attachments,
        targets: null,
      };

      await sendInternalNote(payload);
      closeInternalNote();
      if (personalInboxId !== null) {
        await personalInbox(personalInboxId, 0, "", undefined, true);
        await selectPersonalPaginatedThread(threadDetail.id, personalInboxId);
      } else {
        await refresh(0, "", undefined, true);
        await selectPaginatedThread(threadDetail.id);
      }
      UIkit.notification("Internal note saved.", "success");
    } catch (error) {
      const statusCode = error?.response?.status;
      const message = error?.response?.data?.error;

      if (statusCode === 401 || statusCode === 403) {
        setInternalNoteError(
          "You do not have permission to add internal notes to this thread.",
        );
      } else {
        setInternalNoteError(
          message || "Unable to save internal note. Please try again.",
        );
      }
    } finally {
      setInternalNoteSaving(false);
    }
  };

  const handleImage = (e) => {
    var files = e.target.files;

    if (files.length) {
      setAttachment(files[0]);
      if (!isInputValid) {
        setIsInputValid(true);
      }
    } else {
      setAttachment(null);
    }
  };

  const alterAssetUrl = (url) => {
    return url
      ?.replace("http://uploads.ruebarue.com", "https://public.ruebarue.com")
      ?.replace(
        "s3.us-east-2.amazonaws.com/uploads.ruebarue.com",
        "public.ruebarue.com",
      );
  };

  const getSender = (msg) => {
    if (msg.message_source === "airbnb") {
      if (msg.sender_type === "guest") {
        let source = threadDetail.sources.find((s) => s.ota === "airbnb");
        let reses = source.thread?.reservation || [];
        let guest = (reses[0] || {}).guest || {};

        guest.sender_type = "guest";
        guest.last_name = "Via AirBnB";

        return guest;
      }

      if (msg.sender_type === "system") {
        return {
          sender_type: "system",
          first_name: "Host",
          last_name: "Via AirBnB",
        };
      }
    }

    let { recipients } = inbox;
    let { guest_reservations, contact } = threadDetail;

    if (msg.sender_type === "automated") {
      return { last_name: "Automated" };
    } else if (msg.sender_type === "recipient") {
      return recipients.find((r) => r.recipient_id === msg.sender_id);
    } else if (msg.sender_type === "internal") {
      return (
        recipients.find((r) => r.recipient_id === msg.sender_id) ||
        recipients.find((r) => r.user_id === msg.sender_id) ||
        msg.users?.[0] ||
        { first_name: "Team", last_name: "Member" }
      );
    } else if (msg.sender_type === "user") {
      return (
        recipients.find((r) => r.user_id === msg.sender_id) ||
        recipients.find((r) => r.recipient_id === msg.sender_id)
      );
    } else if (msg.sender_type === "guest") {
      return selectPreferredReservation(guest_reservations);
    } else if (msg.sender_type === "leads") {
      if (Array.isArray(guest_reservations) && guest_reservations.length > 0) {
        return selectPreferredReservation(guest_reservations);
      } else {
        return contact;
      }
    } else {
      return contact;
    }
  };

  const handleAddWorkOrder = useCallback(() => {
    workOrderContext.setIsNew(true);
    workOrderContext.initWorkOrder();
  }, []);

  const renderAttachments = useCallback(
    (msg) => {
      const attachments = msg.attachments || [];

      if (!attachments.length) {
        return null;
      }

      return (
        <div className="rbr-msg-attachment-list">
          {attachments.map((messageAttachment, idx) => {
            if (messageAttachment.type === "image") {
              return (
                <Image
                  key={`${msg.message_id || "msg"}_img_${idx}`}
                  src={alterAssetUrl(messageAttachment.url)}
                />
              );
            }

            if (messageAttachment.type === "video") {
              return (
                <video
                  key={`${msg.message_id || "msg"}_video_${idx}`}
                  src={messageAttachment.url}
                  controls
                />
              );
            }

            return (
              <a
                key={`${msg.message_id || "msg"}_file_${idx}`}
                className="rbr-msg-file-attachment"
                href={messageAttachment.url}
                target="_blank"
                rel="noreferrer"
              >
                <FeatherIcon name="paperclip" className="msg-icon" />
                <span>
                  {messageAttachment.original_name ||
                    messageAttachment.name ||
                    "Attachment"}
                </span>
              </a>
            );
          })}
        </div>
      );
    },
    [alterAssetUrl],
  );

  const handleDownloadImage = (msg) => {
    let images = (msg.attachments || []).filter((a) => a.type === "image");

    for (let i = 0; i < images.length; i++) {
      fetch(images[i].url)
        .then((response) => response.blob())
        .then((blob) => {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = images[i].original_name || `image_${i + 1}`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        })
        .catch((error) => {
          console.error("Download failed", error);
        });
    }
  };

  const handleCopyImage = async (msg) => {
    if (!navigator.clipboard || !window.ClipboardItem) {
      console.error(
        "Clipboard API or ClipboardItem is not supported in this browser.",
      );
      return;
    }

    let images = (msg.attachments || []).filter((a) => a.type === "image");

    if (images.length === 0) {
      console.error("No images found in the message attachments.");
      return;
    }

    try {
      // Fetch the first image
      const response = await fetch(images[0].url);
      if (!response.ok) {
        console.error("Failed to fetch image, status:", response.status);
        return;
      }

      // Create an image element
      const img = new Image();
      img.crossOrigin = "anonymous"; // Prevent CORS issues

      img.src = images[0].url;
      img.onload = async () => {
        // Create a canvas and draw the image onto it
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);

        // Convert canvas to blob in PNG format
        canvas.toBlob(async (blob) => {
          if (blob) {
            // Write the PNG blob to the clipboard
            const clipboardItem = new ClipboardItem({ "image/png": blob });
            await navigator.clipboard.write([clipboardItem]);

            UIkit.notification("Image Copied", "success");
            console.log("Image copied to clipboard!");
          } else {
            console.error("Failed to convert image to PNG.");
          }
        }, "image/png"); // Force PNG conversion
      };
    } catch (error) {
      console.error("Failed to copy image: ", error);
    }
  };

  const mostRecentGuestMsgKey = useMemo(() => {
    const guestMsgs = (messages || []).filter(
      (m) => m?.sender_type === "guest",
    );
    if (!guestMsgs.length) return null;

    const newest = guestMsgs.reduce((best, cur) => {
      const bestT = new Date(best?.created_at || 0).getTime();
      const curT = new Date(cur?.created_at || 0).getTime();
      return curT > bestT ? cur : best;
    });

    // prefer message_id if you have it; fall back to created_at
    return newest?.message_id || String(newest?.created_at);
  }, [messages]);

  return (
    <>
      <section id="hms-message-page">
        <AddTextLine feature="Messages" />
        <div
          id="hms-page-title"
          style={{ display: "flex", alignItems: "center" }}
        >
          <h1 className="uk-heading-small">Messages</h1>
          <div
            className="uk-flex uk-flex-middle"
            style={{ marginLeft: "auto", gap: "8px" }}
          >
            {granted !== "granted" && (
              <button
                type="button"
                className="uk-button hms-btn hms-white-btn"
                onClick={handleNotification}
                disabled={granted === "denied"}
                uk-tooltip="Enable Desktop Notification"
              >
                Enable Desktop Notifications
              </button>
            )}
          </div>
        </div>

        <div id="hms-main-body">
          <div className="uk-overflow-auto">
            <div
              className="uk-flex hms-chat-section"
              style={{ alignItems: "stretch", height: "calc(100vh - 110px)" }}
            >
              <Inbox onNewMessage={() => setNewMsgOpen(true)} />

              {(selectedThreadId > 0 || selectedThreadId === "new") && (
                <div
                  className="uk-card uk-card-default uk-card-small uk-card-body uk-margin-left uk-width-1-2@m hms-msg-cards hms-chat-card"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                  }}
                >
                  {loading ? (
                    <div className="uk-flex uk-flex-center uk-flex-middle">
                      <span data-uk-spinner="ratio: 2;"></span>
                    </div>
                  ) : (
                    <div
                      className="hms-chat-panel"
                      id="hms-chat-panel-scrollable"
                      style={{
                        overflowY: "auto",
                        flex: 1,
                        minHeight: 0,
                        display: "flex",
                        flexDirection: "column-reverse",
                      }}
                    >
                      <InfiniteScroll
                        dataLength={messages.length}
                        next={handleLoadMore}
                        hasMore={hasMore}
                        inverse={true}
                        scrollableTarget="hms-chat-panel-scrollable"
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          overflow: "hidden",
                        }}
                      >
                        {isLoadingMore && (
                          <div style={{ textAlign: "center", padding: 8 }}>
                            <span data-uk-spinner="ratio: 1.5;" />
                          </div>
                        )}
                        {messages.map((msg, index) => {
                          let sender = getSender(msg);
                          const isGuestMsg = msg?.sender_type === "guest";
                          const isInternalNote = msg?.sender_type === "internal";
                          const isAutomatedMsg =
                            msg?.sender_type === "automated" ||
                            msg?.sender_type === "system";

                          const msgKey =
                            msg?.message_id ||
                            String(msg?.created_at) ||
                            String(index);
                          const showAiBtn =
                            isGuestMsg && msgKey === mostRecentGuestMsgKey;

                          return (
                            <div
                              className={`hms-message-wrapper ${msgClasses[msg.sender_type] || "rbr-msg-inbound"}`}
                              key={msg.message_id || index}
                            >
                              <div className="hms-message-content">
                                <div
                                  className={clsx({
                                    "rbr-msg-internal-note-header": isInternalNote,
                                  })}
                                >
                                  {isAutomatedMsg ? (
                                    <div
                                      className="hms-messenger-name automated rbr-msg-type-icon"
                                    >
                                      <FeatherIcon
                                        name="cpu"
                                        className="msg-icon"
                                      />
                                      <span>Automated</span>
                                    </div>
                                  ) : (
                                    <div
                                      className={`hms-messenger-name ${msg?.sender_type}`}
                                    >
                                      {sender?.first_name || ""}{" "}
                                      {sender?.last_name || "[Unknown]"}
                                    </div>
                                  )}
                                  {isInternalNote && (
                                    <div
                                      className="hms-messenger-name automated rbr-msg-internal-note-label"
                                    >
                                      <FeatherIcon
                                        name="file-text"
                                        className="msg-icon"
                                      />
                                      <span>Internal Note</span>
                                    </div>
                                  )}
                                </div>
                                <div
                                  className={clsx("hms-message-body", {
                                    undelivered: msg.status === "undelivered",
                                    "rbr-msg-internal-note-body": isInternalNote,
                                  })}
                                  onMouseEnter={() => setMsgId(msg.message_id)}
                                >
                                  {/* Render message content with empty line between sentences, but do not break URLs */}
                                  <div className="rbr-msg-row">
                                    <div className="rbr-msg-text">
                                      {msg.content}
                                    </div>

                                    {showAiBtn && (
                                      <button
                                        type="button"
                                        className="rbr-ai-suggest-btn"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          fetchAiSuggestion(msg.content);
                                        }}
                                        title="Suggest AI reply"
                                        disabled={aiLoading}
                                        uk-tooltip="Suggest AI reply"
                                      >
                                        <FeatherIcon
                                          name="zap"
                                          className="ai-suggest-icon"
                                        />
                                      </button>
                                    )}
                                  </div>
                                  {renderAttachments(msg)}
                                </div>

                                <div
                                  className={clsx("hms-message-property", {
                                    "rbr-msg-internal-note-meta": isInternalNote,
                                  })}
                                >
                                  <div className="status">
                                    {!isInternalNote && msg.error_code !== "" ? (
                                      <>
                                        <img
                                          className="report-warning-icon"
                                          data-src="/images/warning.svg"
                                          data-uk-img=""
                                          loading="lazy"
                                          src="/images/warning.svg"
                                          alt="Warning Icon"
                                          data-uk-tooltip={`title: ${msg.status || "undelivered"} - ${msg.error_code} - ${msg.error_message}`}
                                        />
                                        Undelivered
                                      </>
                                    ) : (
                                      !isInternalNote &&
                                      msg.attachments &&
                                      msg.attachments.findIndex(
                                        (a) => a.type === "image",
                                      ) > -1 && (
                                        <>
                                          <button
                                            className="uk-button hms-simple-icon-btn"
                                            onClick={() =>
                                              handleDownloadImage(msg)
                                            }
                                            data-uk-tooltip="Download Photo"
                                          >
                                            <FeatherIcon
                                              name="download"
                                              className="msg-icon"
                                            />
                                          </button>

                                          <button
                                            className="uk-button hms-simple-icon-btn"
                                            onClick={() => handleCopyImage(msg)}
                                            data-uk-tooltip="Copy to Clipboard"
                                          >
                                            <FeatherIcon
                                              name="clipboard"
                                              className="msg-icon"
                                            />
                                          </button>
                                        </>
                                      )
                                    )}
                                  </div>
                                  <time
                                    style={{ display: "flex" }}
                                    className={`${msg.sender_type}`}
                                  >
                                    {moment
                                      .utc(msg.created_at)
                                      .local()
                                      .format("MMM DD, YYYY hh:mm a")}
                                  </time>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                        {(typingStatus || []).map((pid) => {
                          return (
                            <div
                              className="hms-message-wrapper rbr-msg-typing"
                              key={pid}
                            >
                              <div className="hms-message-content">
                                <div className="hms-message-body">
                                  <div className="rbr-msg-row">
                                    <div className="rbr-msg-text">
                                      <div className="bouncing-ellipse">
                                        <span>.</span>
                                        <span>.</span>
                                        <span>.</span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div className="hms-messenger-name user">
                                  <i>{personalInboxName(pid)} is typing</i>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </InfiniteScroll>
                    </div>
                  )}

                  <div className="hms-msg-typing-box uk-margin">
                    <textarea
                      className={classnames("uk-textarea", {
                        error: !isInputValid,
                      })}
                      rows="4"
                      style={{
                        resize: "none",
                        minHeight: "96px",
                        maxHeight: "96px",
                        overflowY: "auto",
                      }}
                      placeholder="Write a response"
                      onChange={handleValidate}
                      onPaste={handleMessagePaste}
                      value={sendText}
                      maxLength={
                        carrier === "sms" ? SMS_CHARACTER_LIMIT : undefined
                      }
                    ></textarea>
                    {carrier === "sms" && (
                      <>
                        <div className="messages-sms-length-footer">
                          <MessageLengthInlineStatus analysis={messageLength} />
                          <div className="hms-small-note">
                            Characters Remaining:{" "}
                            {SMS_CHARACTER_LIMIT - sendText.length}
                          </div>
                        </div>
                        <div className="messages-sms-length-guidance">
                          <MessageLengthEditorStatus analysis={messageLength} />
                        </div>
                      </>
                    )}
                    {smsOptimization && (
                      <div className="uk-alert-primary sms-ai-optimization-result">
                        <strong>Rue AI suggestion</strong>
                        <div className="uk-margin-small-top uk-text-break">
                          {smsOptimization.suggested_message}
                        </div>
                        <div className="uk-margin-small-top">
                          <button
                            type="button"
                            className="uk-button hms-btn hms-red-btn uk-button-small"
                            onClick={applyOptimizedReply}
                          >
                            Use suggestion
                          </button>
                          <button
                            type="button"
                            className="uk-button hms-btn hms-white-btn"
                            onClick={() => setSmsOptimization(null)}
                          >
                            Dismiss
                          </button>
                        </div>
                      </div>
                    )}
                    {smsOptimizationError && (
                      <div className="uk-alert-danger sms-ai-optimization-result">
                        {smsOptimizationError}
                      </div>
                    )}
                    <div className="msg-typing-btn">
                      {!!attachment && (
                        <div className="rbr-msg-attachments">
                          <div className="rbr-msg-attachment">
                            <img
                              src={URL.createObjectURL(attachment)}
                              alt={attachment.name}
                              className="attachment-thumbnail"
                            />
                            <button
                              type="button"
                              className="rbr-msg-attachment-remove"
                              onClick={() => setAttachment(null)}
                            >
                              <FeatherIcon
                                name="x"
                                className="attachment-remove-icon"
                              />
                            </button>
                          </div>
                        </div>
                      )}
                      <div className="uk-flex">
                        <div className="uk-flex uk-flex-middle uk-flex-1">
                          <div>
                            <button
                              className={`uk-button hms-btn hms-red-btn uk-margin-small-right ${sending ? "disabled" : ""}`}
                              type="button"
                              onClick={() =>
                                handleSendMessage(selectedThreadId)
                              }
                            >
                              {isSendingFlag ? "Sending ..." : "Send"}
                            </button>
                          </div>
                          <div
                            className="msg-editing-btn"
                            style={{ display: "flex" }}
                          >
                            <div className="messages-rue-ai-actions">
                              <Responses onChange={handleSelectedResponse} />
                              {carrier === "sms" && (
                                <button
                                  type="button"
                                  className="sms-ai-optimize-icon"
                                  onClick={handleOptimizeReply}
                                  disabled={
                                    smsOptimizationLoading || !sendText.trim()
                                  }
                                  aria-label="Optimize with Rue AI"
                                  title="Optimize with Rue AI"
                                  uk-tooltip="Optimize with Rue AI"
                                >
                                  {smsOptimizationLoading ? (
                                    <span data-uk-spinner="ratio: 0.45"></span>
                                  ) : (
                                    <img
                                      src="/images/ai-guest-reply.svg"
                                      alt=""
                                    />
                                  )}
                                </button>
                              )}
                            </div>
                            {selectedThreadId > 0 && (
                              <button
                                type="button"
                                uk-tooltip="Internal Note"
                                onClick={() => setInternalNoteOpen(true)}
                              >
                                <FeatherIcon
                                  name="file-text"
                                  className="msg-icon"
                                />
                              </button>
                            )}
                            <div>
                              <button uk-tooltip="Send an emoji" type="button">
                                <FeatherIcon
                                  name="smile"
                                  className="msg-icon"
                                />
                              </button>

                              <EmojiPicker
                                onSelect={(emoji) => handleEmojiClick(emoji)}
                              />
                            </div>
                            <div data-uk-form-custom>
                              <button uk-tooltip="Send Photo">
                                <input
                                  ref={inputRef}
                                  type="file"
                                  // multiple
                                  onChange={handleImage}
                                />
                                <FeatherIcon
                                  name="image"
                                  className="msg-icon"
                                />
                              </button>
                            </div>
                            <button
                              uk-tooltip="Add a Task"
                              uk-toggle="target: #work-order-add"
                              onClick={handleAddWorkOrder}
                            >
                              <FeatherIcon name="tool" className="msg-icon" />
                            </button>
                          </div>
                        </div>

                        <div className="msg-statuses">
                          {!!(viewingStatus || []).length && (
                            <div className="msg-status" tabIndex={0}>
                              <span className="msg-status-trigger">
                                <FeatherIcon
                                  name="eye"
                                  className="inbox-action-icon"
                                  color="inherit"
                                />
                                <span className="msg-status-count">
                                  {viewingStatus.length}
                                </span>
                              </span>
                              <div className="msg-status-list" role="tooltip">
                                <div className="msg-status-list-header">
                                  <FeatherIcon
                                    name="eye"
                                    className="msg-status-list-icon"
                                  />
                                  <span>
                                    Viewing Now
                                    <span className="msg-status-list-count">
                                      {viewingStatus.length}
                                    </span>
                                  </span>
                                </div>
                                <ul className="msg-status-list-items">
                                  {(viewingStatus || []).map((s) => {
                                    const name = personalInboxName(s);
                                    const initial = (name || "?")
                                      .trim()
                                      .charAt(0)
                                      .toUpperCase();
                                    return (
                                      <li key={`view-${s}`}>
                                        <span className="msg-status-avatar">
                                          {initial}
                                        </span>
                                        <span className="msg-status-name">
                                          {name}
                                        </span>
                                      </li>
                                    );
                                  })}
                                </ul>
                              </div>
                            </div>
                          )}
                          {!!(readStatus || []).length && (
                            <div className="msg-status" tabIndex={0}>
                              <span className="msg-status-trigger">
                                <FeatherIcon
                                  name="check-circle"
                                  className="inbox-action-icon"
                                  color="inherit"
                                />
                                <span className="msg-status-count">
                                  {readStatus.length}
                                </span>
                              </span>
                              <div className="msg-status-list" role="tooltip">
                                <div className="msg-status-list-header">
                                  <FeatherIcon
                                    name="check-circle"
                                    className="msg-status-list-icon"
                                  />
                                  <span>
                                    Seen By
                                    <span className="msg-status-list-count">
                                      {readStatus.length}
                                    </span>
                                  </span>
                                </div>
                                <ul className="msg-status-list-items">
                                  {(readStatus || []).map((s) => {
                                    const name = personalInboxName(s);
                                    const initial = (name || "?")
                                      .trim()
                                      .charAt(0)
                                      .toUpperCase();
                                    return (
                                      <li key={`read-${s}`}>
                                        <span className="msg-status-avatar">
                                          {initial}
                                        </span>
                                        <span className="msg-status-name">
                                          {name}
                                        </span>
                                      </li>
                                    );
                                  })}
                                </ul>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {selectedThreadId > 0 && (
                <aside
                  className="uk-margin-left uk-width-1-4@m hms-form-card"
                  uk-viewport-box=""
                >
                  {getThreadType(threadDetail) === "sms" && (
                    <GuestDetails
                      contact={contact}
                      guest={guest}
                      guest_reservations={guestReservations}
                    />
                  )}

                  {getThreadType(threadDetail) === "airbnb" && (
                    <AirbnbDetails thread={threadDetail} />
                  )}
                </aside>
              )}
            </div>
          </div>
        </div>
      </section>
      <WorkOrderAdd msg_gr={guestReservations[0]} msgId={msgId} showDelete={false} />
      {newMsgOpen && (
        <Suspense fallback={null}>
          <NewMessageModal
            open={newMsgOpen}
            onClose={() => setNewMsgOpen(false)}
            onMessageSent={handleNewMessageSent}
          />
        </Suspense>
      )}
      {aiOpen && (
        <div
          className="uk-modal uk-open"
          style={{ display: "block" }}
          onClick={(e) => {
            // close when clicking backdrop
            if (e.target.classList.contains("uk-modal")) closeAiModal();
          }}
        >
          <div className="uk-modal-dialog uk-modal-body">
            <button
              className="uk-modal-close-default"
              type="button"
              uk-close="true"
              onClick={closeAiModal}
            />

            <h3 className="uk-modal-title">AI Suggested Response</h3>

            <div className="uk-text-meta uk-margin-small-bottom">
              For: “{aiQuestion || "—"}”
            </div>

            {aiLoading && (
              <div className="uk-flex uk-flex-middle uk-margin">
                <span uk-spinner="ratio: 1" />
                <span className="uk-margin-small-left">
                  Thinking through the best reply...
                </span>
              </div>
            )}

            {aiError && (
              <div className="uk-alert-danger" uk-alert="true">
                {aiError}
              </div>
            )}

            {!aiLoading && !aiError && (
              <>
                <pre
                  className="uk-text-break"
                  style={{
                    maxHeight: 320,
                    overflow: "auto",
                    padding: 16,
                    fontWeight: 500,
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {aiAnswer || "No answer returned."}
                </pre>

                {!!aiSource && (
                  <div className="uk-text-meta uk-margin-small-top">
                    Source: {aiSource}
                  </div>
                )}
              </>
            )}

            <div className="uk-flex uk-flex-right uk-margin-top">
              <button
                className="uk-button hms-btn uk-offcanvas-close"
                onClick={closeAiModal}
              >
                Cancel
              </button>

              <button
                className="uk-button hms-btn hms-red-btn uk-margin-small-right"
                onClick={insertAiResponse}
                disabled={!aiAnswer || aiLoading}
              >
                Insert response
              </button>
            </div>
          </div>
        </div>
      )}
      <InternalNoteOffcanvas
        open={internalNoteOpen}
        note={internalNoteText}
        attachments={internalNoteAttachments}
        saving={internalNoteSaving}
        error={internalNoteError}
        onClose={closeInternalNote}
        onNoteChange={setInternalNoteText}
        onAttachmentChange={handleInternalAttachmentChange}
        onRemoveAttachment={(index) => {
          setInternalNoteAttachments((prev) =>
            prev.filter((_, attachmentIndex) => attachmentIndex !== index),
          );
        }}
        onSave={handleSaveInternalNote}
      />
    </>
  );
};

export default Messages;
